/**
 * Route + navigation registry. Single source of truth for the sidebar and
 * the router — drop a page in here, add a component, done.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-20-v3
 */
import type { LucideIcon } from 'lucide-react';
import {
    Activity,
    AlertTriangle,
    BarChart3,
    Bell,
    Box,
    ClipboardCheck,
    Database,
    Download,
    FileText,
    Home,
    KeyRound,
    LifeBuoy,
    LineChart,
    Mail,
    Megaphone,
    ShieldAlert,
    Tags,
    Users
} from 'lucide-react';

export interface RouteDef {
    path: string;
    label: string;
    icon: LucideIcon;
    section: 'main' | 'content' | 'infra' | 'product' | 'business' | 'admin';
    phase: 1 | 2 | 3 | 4;
    /** Spec filename under docs/specs/ — used for placeholder descriptors. */
    spec: string;
    /** `true` when a real implementation exists; otherwise renders placeholder. */
    built?: boolean;
}

export const ROUTES: RouteDef[] = [
    // Main
    {
        path: '/',
        label: 'Overview',
        icon: Home,
        section: 'main',
        phase: 1,
        spec: 'overview.md',
        built: true
    },
    {
        path: '/users',
        label: 'Users',
        icon: Users,
        section: 'main',
        phase: 1,
        spec: 'users.md',
        built: true
    },

    // Content
    {
        path: '/support',
        label: 'Support',
        icon: LifeBuoy,
        section: 'content',
        phase: 1,
        spec: 'support.md',
        built: true
    },
    {
        path: '/restore',
        label: 'Restore & exports',
        icon: Download,
        section: 'content',
        phase: 4,
        spec: 'restore.md',
        built: true
    },

    // Infrastructure
    {
        path: '/system-health',
        label: 'System health',
        icon: Activity,
        section: 'infra',
        phase: 1,
        spec: 'system-health.md',
        built: true
    },
    {
        path: '/storage',
        label: 'Storage',
        icon: Database,
        section: 'infra',
        phase: 4,
        spec: 'storage.md',
        built: true
    },
    {
        path: '/notifications',
        label: 'Notifications',
        icon: Bell,
        section: 'infra',
        phase: 4,
        spec: 'notifications.md',
        built: true
    },
    {
        path: '/email-templates',
        label: 'Email templates',
        icon: Mail,
        section: 'infra',
        phase: 4,
        spec: 'email-templates.md',
        built: true
    },
    {
        path: '/error-logs',
        label: 'Error logs',
        icon: AlertTriangle,
        section: 'infra',
        phase: 1,
        spec: 'error-logs.md',
        built: true
    },

    // Product
    {
        path: '/help-docs',
        label: 'Help docs',
        icon: FileText,
        section: 'product',
        phase: 4,
        spec: 'help-docs.md',
        built: true
    },
    {
        path: '/taxonomy',
        label: 'Taxonomy',
        icon: Tags,
        section: 'product',
        phase: 3,
        spec: 'taxonomy.md',
        built: true
    },

    // Business
    {
        path: '/billing',
        label: 'Billing',
        icon: LineChart,
        section: 'business',
        phase: 2,
        spec: 'billing.md',
        built: true
    },
    {
        path: '/affiliate-manager',
        label: 'Affiliate manager',
        icon: Megaphone,
        section: 'business',
        phase: 2,
        spec: 'affiliate-manager.md',
        built: true
    },
    {
        path: '/analytics',
        label: 'Analytics',
        icon: BarChart3,
        section: 'business',
        phase: 2,
        spec: 'analytics.md',
        built: true
    },

    // Admin
    {
        path: '/privacy-terms',
        label: 'Privacy & terms',
        icon: ClipboardCheck,
        section: 'admin',
        phase: 4,
        spec: 'compliance.md',
        built: true
    },
    {
        path: '/admin-audit',
        label: 'Admin & audit',
        icon: ShieldAlert,
        section: 'admin',
        phase: 4,
        spec: 'admin-audit.md',
        built: true
    }
];

export const SECTION_LABELS: Record<RouteDef['section'], string> = {
    main: 'Main',
    content: 'Content',
    infra: 'Infrastructure',
    product: 'Product',
    business: 'Business',
    admin: 'Admin'
};

export const SECTION_ORDER: RouteDef['section'][] = [
    'main',
    'content',
    'infra',
    'product',
    'business',
    'admin'
];

export function findRoute(pathname: string): RouteDef | undefined {
    const exact = ROUTES.find((r) => r.path === pathname);
    if (exact) return exact;
    return ROUTES.find((r) => r.path !== '/' && pathname.startsWith(r.path));
}

// Placeholder icon re-export for pages that need it directly.
export { Box as PlaceholderIcon, KeyRound, ClipboardCheck };
